﻿import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiChevronRight, FiMail, FiTrash2, FiMapPin } from 'react-icons/fi';
import type { Visit } from '@/hooks/useVisits';
import { hasCoordinates } from '@/utils/mapUtils';
import MapModal from '@/components/common/MapModal';

interface Props {
  visits: Visit[];
  onView: (visit: Visit) => void;
  onDelete: (visit: Visit) => void;
  onDownload: (visit: Visit) => void;
  onEmail: (visit: Visit) => void;
}

export default function VisitTable({ visits, onView, onDelete, onDownload, onEmail }: Props) {
  const { t } = useTranslation();
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number; title?: string; address?: string } | null>(null);

  if (visits.length === 0) {
    return <p className="text-center text-muted py-4">{t('labels.noResults')}</p>;
  }

  return (
    <>
      <table className="table-modern">
        <thead>
          <tr>
            <th>{t('labels.company')}</th>
            <th>{t('labels.customer')}</th>
            <th>Data visita</th>
            <th>Nazione</th>
            <th className="text-center">{t('labels.version')}</th>
            <th>Data revisione</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visits.map(v => {
            const coords = hasCoordinates(v as Record<string, unknown>);
            return (
              <tr key={v.id + '-' + v.versione}>
                <td data-label={t('labels.company')}>{v.codazienda}</td>
                <td data-label={t('labels.customer')}>
                  <div>
                    <small className="text-muted" style={{ fontSize: 10 }}>[{v.codcliente}]</small>{' '}
                    <span className="fw-semibold" style={{ fontSize: 13 }}>{v.ragsociale}</span>
                    {v.citta && <small className="text-muted"> {v.citta}</small>}
                  </div>
                  {v.usercreate && (
                    <p className="mb-0 text-xs text-muted">Creato da <strong>{v.usercreate}</strong></p>
                  )}
                  {v.versione > 1 && v.userupdate && (
                    <p className="mb-0 text-xs text-muted">Modificato da <strong>{v.userupdate}</strong></p>
                  )}
                </td>
                <td data-label="Data visita">{formatDateStr(v.datavisita)}</td>
                <td data-label="Nazione">{v.desnazione || ''}</td>
                <td data-label={t('labels.version')} className="text-center">{v.versione}</td>
                <td data-label="Data revisione">{formatDateStr(v.datains)}</td>
                <td>
                  <span className="d-flex align-items-center justify-content-end gap-1">
                    {coords && (
                      <Button variant="outline-secondary" size="sm" className="btn-icon"
                        onClick={() => setMapCoords({
                          ...coords,
                          title: v.ragsociale,
                          address: [v.indirizzo, v.cap, v.citta, v.prov, v.nazione].map(x => String(x || '').trim()).filter(Boolean).join(', '),
                        })} title="Vedi su mappa"><FiMapPin /></Button>
                    )}
                    <Button variant="outline-secondary" size="sm" className="btn-icon"
                      onClick={() => onDownload(v)} title="Scarica report"><FiDownload /></Button>
                    {v.mostrabtnemail && (
                      <Button variant="outline-secondary" size="sm" className="btn-icon"
                        onClick={() => onEmail(v)} title="Invia email"><FiMail /></Button>
                    )}
                    {v.mostrabtnelimina && (
                      <Button variant="outline-secondary" size="sm" className="btn-icon"
                        onClick={() => onDelete(v)} title="Elimina"><FiTrash2 /></Button>
                    )}
                    <Button variant="outline-secondary" size="sm" className="btn-icon"
                      onClick={() => onView(v)} title="Dettaglio"><FiChevronRight /></Button>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {mapCoords && (
        <MapModal show={!!mapCoords} onHide={() => setMapCoords(null)}
          lat={mapCoords.lat} lng={mapCoords.lng} title={mapCoords.title} address={mapCoords.address} />
      )}
    </>
  );
}

function formatDateStr(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const lang = sessionStorage.getItem('lang') || 'it';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return lang === 'en' ? month + '/' + day + '/' + year : day + '-' + month + '-' + year;
  } catch { return dateStr; }
}
